import React from 'react';

/**
 * Catches errors in the VideoCall subtree so the app doesn't crash.
 * Shows a toast and a fallback UI (close button) so the user can dismiss the call.
 */
class VideoCallErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const msg = error?.message || 'Video call error';
    // Both providers: OpenTok names on the left, the livekit-client equivalents
    // on the right. Without the LiveKit names a MediTalk media failure would be
    // reported as a generic app crash instead of a recoverable video issue.
    const isMediaStackError =
      /getStats|MediaStreamTrack|localDescription|RTCPeerConnection|OpenTok|SessionDispatcher|opentok|LiveKit|livekit|SignalClient|ConnectionError|PublishTrackError|TrackInvalidError|UnexpectedConnectionState|DeviceUnsupportedError|MediaDeviceFailure|NegotiationError/i.test(
        msg
      ) ||
      (errorInfo?.componentStack &&
        /VideoCall|opentok|livekit/i.test(errorInfo.componentStack));
    // Log to console for debugging but avoid user-facing toasts.
    // eslint-disable-next-line no-console
    console.error(
      isMediaStackError
        ? 'Video connection had a temporary issue. You can continue or end the call.'
        : 'Something went wrong with the video call. You can close and try again.',
      error
    );
  }

  handleClose = () => {
    this.setState({ hasError: false, error: null });
    this.props.onClose?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-6 bg-[#0f0f12] text-white min-h-[200px] rounded-2xl">
          <p className="text-center mb-4">Video call encountered an issue.</p>
          <button
            type="button"
            onClick={this.handleClose}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg"
          >
            Close
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default VideoCallErrorBoundary;
